import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import PageHeader from '../components/PageHeader';
import styles from './Trailers.module.css';

const GENRES = ['All','Action','Thriller','Horror','Comedy','Adventure','Sci-Fi','Family','Musical','Drama'];

const TIER_INFO = [
  { name: 'Bronze',  min: 5,  color: '#CD7F32', desc: 'Film ticket + streaming access' },
  { name: 'Silver',  min: 10, color: '#C0C0C0', desc: 'Behind-the-scenes content' },
  { name: 'Gold',    min: 25, color: '#F5C518', desc: 'Event access + merch discounts' },
  { name: 'Diamond', min: 50, color: '#B9F2FF', desc: "Director Q&A + early screening" },
];

const SEED_TRAILERS = [
  {
    id: 'dark-knight', slug: 'the-dark-knight',
    title: 'The Dark Knight', year: '2008',
    genres: ['Action', 'Thriller'],
    thumbnail_url: '/posters/Dark_Knight_Poster.jpg',
    trailer_url: 'https://www.youtube.com/watch?v=EXeTwQWrcwY',
    vote_count: 12400, vote_goal: 95000,
    round_end: new Date(Date.now() + 90 * 24 * 3600 * 1000).toISOString(),
    description: 'When a menace known as the Joker wreaks havoc and chaos on the people of Gotham, Batman, James Gordon and Harvey Dent must work together to put an end to the madness.',
    director: ['Christopher Nolan'],
    writers: ['Jonathan Nolan', 'Christopher Nolan', 'David S. Goyer'],
    stars: ['Christian Bale', 'Heath Ledger', 'Aaron Eckhart'],
  },
  {
    id: 'mission-impossible', slug: 'mission-impossible-the-final-reckoning',
    title: 'Mission: Impossible – The Final Reckoning', year: '2025',
    genres: ['Action'],
    thumbnail_url: '/posters/mi-reckoning-web.jpg',
    trailer_url: 'https://www.youtube.com/watch?v=avz06PDqDbM',
    vote_count: 8000, vote_goal: 90000,
    round_end: new Date(Date.now() + 90 * 24 * 3600 * 1000).toISOString(),
    description: "Hunt and the IMF pursue a dangerous AI called the Entity that's infiltrated global intelligence. With governments and a figure from his past in pursuit, Hunt races to stop it from forever changing the world.",
    director: ['Christopher McQuarrie'],
    writers: ['Bruce Geller', 'Christopher McQuarrie', 'Erik Jendresen'],
    stars: ['Tom Cruise', 'Hayley Atwell', 'Ving Rhames'],
  },
  {
    id: 'dolls-of-voodoo', slug: 'dolls-of-voodoo',
    title: 'Dolls of Voodoo', year: '2013',
    genres: ['Horror', 'Thriller'],
    thumbnail_url: '/posters/Dolls_of_VooDoo.jpg',
    trailer_url: 'https://www.youtube.com/watch?v=ZdSM7mWlZkA',
    vote_count: 3200, vote_goal: 40000,
    round_end: new Date(Date.now() + 90 * 24 * 3600 * 1000).toISOString(),
    description: 'Successful newlyweds become the object of a powerful Voodoo Priest who exacts revenge for his scorned daughter.',
    director: ['Marc Cayce', 'Kenya Moore'],
    writers: ['Obba Babatundé', 'Marc Cayce', 'Johnny Desarmes'],
    stars: ['Kenya Moore', 'Rudolph Moise', 'Obba Babatundé'],
  },
];

const REMOVED_TRAILERS = ['black bag', 'red one', 'mortal engines'];

// H17: Frontend YouTube URL overrides keyed by trailer slug. Used as fallback
// when the backend record has no video_url populated. Once the backend is
// fully populated, these entries can be removed safely — the lookup chain
// (backend video_url → override → SEED trailer_url) means backend always wins.
const TRAILER_URL_OVERRIDES = {
  'the-dark-knight':                          'https://www.youtube.com/watch?v=EXeTwQWrcwY',
  'mission-impossible-the-final-reckoning':   'https://www.youtube.com/watch?v=fsQgc9pCyDU',
  'dolls-of-voodoo':                          'https://www.youtube.com/watch?v=HC-kqdFwPxw',
};

function useCountdown(target) {
  const getTime = useCallback(() => {
    const end = target ? new Date(target) : new Date(Date.now() + 90 * 24 * 3600 * 1000);
    const diff = Math.max(0, end - Date.now());
    return {
      d: Math.floor(diff / 86400000),
      h: Math.floor((diff % 86400000) / 3600000),
      m: Math.floor((diff % 3600000) / 60000),
      s: Math.floor((diff % 60000) / 1000),
    };
  }, [target]);
  const [t, setT] = useState(getTime);
  useEffect(() => {
    setT(getTime());
    const id = setInterval(() => setT(getTime()), 1000);
    return () => clearInterval(id);
  }, [getTime]);
  return t;
}

export function TrailersList() {
  const [trailers, setTrailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genre, setGenre] = useState('All');
  const [votingEnd, setVotingEnd] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.getTrailers({ status: 'active' })
      .then(data => {
        const filtered = Array.isArray(data)
          ? data.filter(t => !REMOVED_TRAILERS.includes(t.title?.toLowerCase?.()))
          : [];
        const combined = filtered.length > 0 ? filtered : SEED_TRAILERS;
        setTrailers(combined);
        const dates = combined
          .filter(t => t.round_end || t.voting_ends_at)
          .map(t => new Date(t.round_end || t.voting_ends_at));
        setVotingEnd(dates.length > 0
          ? new Date(Math.min(...dates)).toISOString()
          : new Date(Date.now() + 90 * 24 * 3600 * 1000).toISOString());
      })
      .catch(() => {
        setTrailers(SEED_TRAILERS);
        setVotingEnd(new Date(Date.now() + 90 * 24 * 3600 * 1000).toISOString());
      })
      .finally(() => setLoading(false));
  }, []);

  const cd = useCountdown(votingEnd);
  const filtered = genre === 'All' ? trailers : trailers.filter(t => t.genres?.includes(genre));

  return (
    <div className={styles.listPage}>
      <PageHeader title="trailers" />

      <div className={styles.hollywoodBlurb}>
        <h3 className={styles.hollywoodTitle}>How to <span>Stick</span> it to Hollywood</h3>
        <ol className={styles.hollywoodList}>
          <li><span>Watch</span> trailers until you find "the one." (You'll know.)</li>
          <li><span>Vote</span> for your favorites with your wallet. $1 = 1 vote. Easy math.</li>
          <li><span>Repeat</span> to earn exclusive perks, insider access, and bragging rights.</li>
        </ol>
      </div>

      <div className={styles.topBar}>
        <div className={styles.comingLabel}>
          <span className={styles.comingAccent}>Coming</span> Soon → Cast Your Vote
        </div>
        <div className={styles.countdownWrap}>
          <div className={styles.countdownLabel}>voting closes in...</div>
          <div className={styles.countdownBoxes}>
            {[['d','Days'],['h','Hours'],['m','Minutes'],['s','Seconds']].map(([k,l]) => (
              <div key={k} className={styles.cBox}>
                <span className={styles.cNum}>{String(cd[k]).padStart(2,'0')}</span>
                <span className={styles.cLabel}>{l}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.filterRow}>
        {GENRES.map(g => (
          <button key={g}
            className={`${styles.filterPill} ${genre === g ? styles.filterActive : ''}`}
            onClick={() => setGenre(g)}>{g}</button>
        ))}
      </div>

      {loading ? (
        <div className="loading-screen"><div className="spinner" /></div>
      ) : (
        <div className={styles.grid}>
          {filtered.length === 0 ? (
            <div className={styles.empty}>
              {genre !== 'All' ? `No ${genre} trailers yet.` : 'No trailers available right now.'}
            </div>
          ) : filtered.map(t => (
            <TrailerCard key={t.id || t.slug} trailer={t}
              onClick={() => navigate(`/trailers/${t.slug}`)} />
          ))}
        </div>
      )}
    </div>
  );
}

function TrailerCard({ trailer: t, onClick }) {
  const pct = t.vote_goal ? Math.min(100, Math.round((t.vote_count / t.vote_goal) * 100)) : 0;
  const thumb = t.thumbnail_url || '/posters/Movie_Posters_1.jpeg';
  return (
    <div className={styles.card} onClick={onClick} role="button" tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}>
      <div className={styles.cardThumb}>
        <img src={thumb} alt={t.title} loading="lazy" />
        <div className={styles.cardOverlay} />
      </div>
      <div className={styles.cardBody}>
        <div className={styles.cardGenres}>
          {(t.genres || []).slice(0, 2).map(g => <span key={g} className="tag">{g}</span>)}
        </div>
        <div className={styles.cardTitle}>{t.title}</div>
        <div className={styles.cardVoteRow}>
          <span className={styles.cardVotes}>votes: {(t.vote_count || 0).toLocaleString()}</span>
          <span className={styles.cardGoal}>goal: {(t.vote_goal || 0).toLocaleString()}</span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className={styles.cardBtn}>
          <button className="btn btn-outline-magenta btn-sm" style={{ width: '100%' }}
            onClick={e => { e.stopPropagation(); onClick(); }}>
            Details &amp; Vote
          </button>
        </div>
      </div>
    </div>
  );
}

export function TrailerDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, updateWalletBalance } = useAuth();
  const { showToast } = useToast();

  const [trailer, setTrailer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [voting, setVoting] = useState(false);
  const [buying, setBuying] = useState(false);
  const [userVotes, setUserVotes] = useState(0);
  const [otherTrailers, setOtherTrailers] = useState([]);

  useEffect(() => {
    api.getTrailer(slug)
      .then(data => {
        setTrailer(data.trailer || data);
        setUserVotes(data.userVotes || 0);
      })
      .catch(() => {
        const found = SEED_TRAILERS.find(t => t.slug === slug);
        if (found) setTrailer(found);
        else navigate('/trailers');
      })
      .finally(() => setLoading(false));

    api.getTrailers({ status: 'active' })
      .then(data => {
        const filtered = Array.isArray(data)
          ? data.filter(t => !REMOVED_TRAILERS.includes(t.title?.toLowerCase?.()) && t.slug !== slug)
          : [];
        const others = filtered.length > 0 ? filtered : SEED_TRAILERS.filter(t => t.slug !== slug);
        setOtherTrailers(others.slice(0, 3));
      })
      .catch(() => setOtherTrailers(SEED_TRAILERS.filter(t => t.slug !== slug).slice(0, 3)));
  }, [slug, navigate]);

  const handleVote = async () => {
    if (!user) { navigate('/login'); return; }
    setVoting(true);
    try {
      const res = await api.vote({ trailerId: trailer.id, votes: qty });
      updateWalletBalance(res.newBalance);
      setUserVotes(v => v + qty);
      setTrailer(t => ({ ...t, vote_count: (t.vote_count || 0) + qty }));
      showToast(`Voted ${qty}x for ${trailer.title}!`, 'success');
    } catch (e) { showToast(e.message, 'error'); }
    finally { setVoting(false); }
  };

  const handleTicket = async () => {
    if (!user) { navigate('/login'); return; }
    setBuying(true);
    try {
      const res = await api.buyTicket(trailer.id);
      updateWalletBalance(res.newBalance);
      showToast('Ticket purchased! See you at the premiere.', 'success');
    } catch (e) { showToast(e.message, 'error'); }
    finally { setBuying(false); }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!trailer) return null;

  const pct = trailer.vote_goal
    ? Math.min(100, Math.round(((trailer.vote_count || 0) / trailer.vote_goal) * 100))
    : 0;

  // Always show poster — use embed only if URL present, else show poster image
  const thumb = trailer.thumbnail_url || '/posters/Movie_Posters_1.jpeg';
  // H17: Lookup priority — backend video_url, then frontend slug-keyed override,
  // then SEED trailer_url. Backend always wins once populated; override is the
  // stopgap; SEED is the local-dev fallback when API throws.
  const rawUrl = trailer.video_url || TRAILER_URL_OVERRIDES[slug] || trailer.trailer_url || '';
  const embedUrl = rawUrl.includes('youtube') || rawUrl.includes('youtu.be')
    ? rawUrl.replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/')
    : null;

  const directors = Array.isArray(trailer.director) ? trailer.director
    : trailer.director ? trailer.director.split(', ') : [];
  const writers = Array.isArray(trailer.writers) ? trailer.writers
    : trailer.writers ? trailer.writers.split(', ') : [];
  const stars = Array.isArray(trailer.stars) ? trailer.stars
    : trailer.stars ? trailer.stars.split(', ') : [];

  return (
    <div className={styles.detailPage}>
      <Link to="/trailers" className={styles.backLink}>← Back to Trailers</Link>

      <div className={styles.detailTop}>
        <div className={styles.detailLeft}>

          {/* Video area — poster covers full area, iframe overlays on top if available */}
          <div className={styles.videoWrap}>
            {/* Poster always visible as background */}
            <img
              src={thumb}
              alt={trailer.title}
              className={styles.videoPoster}
            />
            {/* YouTube embed overlays the poster when available */}
            {embedUrl && (
              <iframe
                src={embedUrl}
                title={trailer.title}
                className={styles.videoIframe}
                allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;fullscreen"
                allowFullScreen
              />
            )}
          </div>

          <div className={styles.detailMeta}>
            <div className={styles.detailGenreTags}>
              {(trailer.genres || []).map(g => <span key={g} className="tag">{g}</span>)}
              {trailer.year && <span className={styles.detailYear}>{trailer.year}</span>}
            </div>
            <h1 className={styles.detailTitle}>{trailer.title}</h1>
            <div className={styles.detailVoteLine}>
              <span className={styles.detailVoteCount}>Votes: {(trailer.vote_count || 0).toLocaleString()}</span>
            </div>
            <div className={styles.credits}>
              {directors.length > 0 && (
                <div className={styles.creditRow}>
                  <span className={styles.creditLabel}>Directed by</span>
                  <span className={styles.creditValue}>{directors.join(', ')}</span>
                </div>
              )}
              {stars.length > 0 && (
                <div className={styles.creditRow}>
                  <span className={styles.creditLabel}>Starring</span>
                  <span className={styles.creditValue}>{stars.join(', ')}</span>
                </div>
              )}
            </div>
            {trailer.description && <p className={styles.detailDesc}>{trailer.description}</p>}
          </div>
        </div>

        {/* Vote panel */}
        <div className={styles.votePanel}>
          <div className={styles.votePanelTop}>
            <div className={styles.voteCount}>{(trailer.vote_count || 0).toLocaleString()}</div>
            <div className={styles.voteGoalText}>/ {(trailer.vote_goal || 0).toLocaleString()} goal</div>
            <div className="progress-bar" style={{ marginTop: 8 }}>
              <div className="progress-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className={styles.votePct}>{pct}% funded</div>
          </div>

          <div className={styles.voteSelect}>
            <div className={styles.voteSelectLabel}>Select to Vote:</div>
            <div className={styles.voteQtyRow}>
              {[1,2,3,4,5,6,7,8,9,10].map(n => (
                <button key={n}
                  className={`${styles.voteQtyBtn} ${qty === n ? styles.voteQtyActive : ''}`}
                  onClick={() => setQty(n)}>{n}V</button>
              ))}
            </div>
            <div className={styles.voteCost}>${qty}.00</div>
          </div>

          {user ? (
            <div className={styles.voteBtns}>
              <button className="btn btn-magenta" style={{ width:'100%' }}
                onClick={handleVote} disabled={voting}>
                {voting ? 'Voting…' : `Vote ${qty}x — $${qty}.00`}
              </button>
              <button className="btn btn-dark" style={{ width:'100%' }}
                onClick={handleTicket} disabled={buying}>
                🎟 Buy My Ticket — $5
              </button>
              {userVotes > 0 && (
                <div className={styles.userVoteNote}>
                  You've cast {userVotes} vote{userVotes !== 1 ? 's' : ''} for this film
                </div>
              )}
            </div>
          ) : (
            <div className={styles.loginNudge}>
              <Link to="/login" className="btn btn-magenta" style={{ width:'100%', justifyContent:'center' }}>Log In to Vote</Link>
              <Link to="/register" className="btn btn-outline-magenta" style={{ width:'100%', justifyContent:'center' }}>Create Account</Link>
            </div>
          )}

          <div className={styles.tiersSection}>
            <div className={styles.tiersTitle}>Supporter Tiers</div>
            {TIER_INFO.map(tier => (
              <div key={tier.name} className={`${styles.tierRow} ${userVotes >= tier.min ? styles.tierUnlocked : ''}`}>
                <span className={styles.tierName} style={{ color: tier.color }}>{tier.name}</span>
                <span className={styles.tierInfo}>
                  <span className={styles.tierVotes}>{tier.min}+ votes</span>
                  {tier.desc}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* About director */}
      {directors.length > 0 && (
        <div className={styles.aboutSection}>
          <h2 className={styles.aboutTitle}>About the Director</h2>
          <div className={styles.aboutGrid}>
            {directors.map(d => (
              <div key={d} className={styles.aboutCard}>
                <div className={styles.aboutAvatar}>{d[0]}</div>
                <div>
                  <div className={styles.aboutName}>{d}</div>
                  <div className={styles.aboutRole}>Director</div>
                </div>
              </div>
            ))}
          </div>
          {writers.length > 0 && (
            <div className={styles.writersRow}>
              <span className={styles.writersLabel}>Written by</span>
              <span className={styles.writersValue}>{writers.join(', ')}</span>
            </div>
          )}
        </div>
      )}

      {/* Other trailers */}
      {otherTrailers.length > 0 && (
        <div className={styles.otherSection}>
          <h2 className={styles.otherTitle}>Other Trailers</h2>
          <div className={styles.otherGrid}>
            {otherTrailers.map(t => (
              <Link
                key={t.slug || t.id}
                to={`/trailers/${t.slug}`}
                className={styles.otherCard}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <img src={t.thumbnail_url || '/posters/Movie_Posters_1.jpeg'} alt={t.title} loading="lazy" />
                <div className={styles.otherBody}>
                  <div className={styles.otherGenres}>
                    {(t.genres || []).slice(0,2).map(g => <span key={g} className="tag" style={{ fontSize:10 }}>{g}</span>)}
                  </div>
                  <div className={styles.otherName}>{t.title}</div>
                  <div className={styles.otherVotes}>
                    <span>votes: {(t.vote_count||0).toLocaleString()}</span>
                    <span>goal: {(t.vote_goal||0).toLocaleString()}</span>
                  </div>
                  {/* H19: Details/Watch buttons removed — the card itself is now the link.
                      Both buttons previously pointed to the same trailer detail URL anyway. */}
                </div>
              </Link>
            ))}
          </div>
          <div style={{ textAlign:'center', marginTop:24 }}>
            <Link to="/trailers" className="btn btn-outline-magenta">← Back to All Trailers</Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default { TrailersList, TrailerDetail };
